import React from 'react';
import styled from 'styled-components';

const Wrap = styled.div`
  max-width: 720px;
  margin: ${(p) => (p.$compact ? '24px' : '48px')} auto 64px;
  padding: 0 24px;
`;

const Hero = styled.div`
  text-align: center;
  margin-bottom: 36px;
`;

const Title = styled.h1`
  margin: 0 0 12px;
  font-size: 28px;
  font-weight: 800;
  color: #1f2937;
`;

const Lead = styled.p`
  margin: 0 auto;
  max-width: 560px;
  font-size: 15px;
  line-height: 1.6;
  color: #6b7280;
`;

const Steps = styled.ol`
  margin: 0 0 32px;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 12px;
`;

const Step = styled.li`
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 16px 18px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
`;

const StepNum = styled.span`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(233, 112, 36, 0.12);
  color: #e97024;
  font-weight: 800;
  font-size: 13px;
  display: grid;
  place-items: center;
`;

const StepBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const StepTitle = styled.div`
  font-weight: 700;
  color: #374151;
  margin-bottom: 4px;
`;

const StepText = styled.div`
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
`;

const SectionTitle = styled.h2`
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FolderList = styled.div`
  display: grid;
  gap: 10px;
  margin-bottom: 20px;
`;

const FolderCard = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  text-align: left;
  padding: 16px 18px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);

  &:hover {
    border-color: rgba(233, 112, 36, 0.35);
    box-shadow: 0 6px 20px rgba(233, 112, 36, 0.12);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
  }
`;

const FolderName = styled.div`
  font-weight: 700;
  color: #1f2937;
`;

const FolderMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
`;

const Badge = styled.span`
  margin-left: auto;
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background: ${(p) =>
    p.$warn ? 'rgba(239,68,68,0.1)' :
    p.$ready ? 'rgba(16, 185, 129, 0.12)' :
    'rgba(59, 130, 246, 0.1)'};
  color: ${(p) =>
    p.$warn ? '#dc2626' :
    p.$ready ? '#059669' :
    '#2563eb'};
`;

const StatRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 5px;
`;

const StatPill = styled.span`
  font-size: 11px;
  color: #6b7280;
  background: rgba(0,0,0,0.04);
  padding: 2px 8px;
  border-radius: 6px;
`;

const EmptyBox = styled.div`
  padding: 24px;
  border-radius: 12px;
  border: 1.5px dashed rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 0.7);
  text-align: center;
  color: #6b7280;
  font-size: 14px;
  line-height: 1.6;
`;

const PrimaryBtn = styled.button`
  margin-top: 16px;
  padding: 10px 18px;
  border: none;
  border-radius: 10px;
  background: #e97024;
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(233, 112, 36, 0.25);

  &:hover {
    filter: brightness(1.05);
  }
`;

export default function ReviewLanding({
  folders,
  loading,
  onSelectFolder,
  onGoToLinkFolders,
}) {
  const drawingFolders = folders.filter((f) => f.drawingCount > 0);
  const otherFolders = folders.filter((f) => f.drawingCount === 0);
  const hasDrawingReviews = drawingFolders.length > 0;

  return (
    <Wrap $compact={hasDrawingReviews}>
      {!hasDrawingReviews && (
        <>
          <Hero>
            <Title>Drawing review</Title>
            <Lead>
              Review linked drawing PDFs here: confirm unrecognized labels, browse labeled
              sheets, and jump to compliance issues on the drawing when a scan flags them.
            </Lead>
          </Hero>

          <Steps>
            <Step>
              <StepNum>1</StepNum>
              <StepBody>
                <StepTitle>Link a folder with drawing PDFs</StepTitle>
                <StepText>
                  On the Link Folders tab, paste a Google Drive or SharePoint folder URL and
                  confirm uploads when prompted.
                </StepText>
              </StepBody>
            </Step>
            <Step>
              <StepNum>2</StepNum>
              <StepBody>
                <StepTitle>Classify PDFs as Drawing deliverables</StepTitle>
                <StepText>
                  In the classifier modal, set each drawing PDF to Deliverable → Drawing so
                  CODiii extracts geometry for review.
                </StepText>
              </StepBody>
            </Step>
            <Step>
              <StepNum>3</StepNum>
              <StepBody>
                <StepTitle>Open the folder below to review sheets</StepTitle>
                <StepText>
                  Pick a sheet in the left explorer, then use <strong>Confirm Labels</strong> to
                  classify unknown annotation types before publishing.
                </StepText>
              </StepBody>
            </Step>
          </Steps>
        </>
      )}

      <SectionTitle>{hasDrawingReviews ? 'Drawing reviews' : 'Your linked folders'}</SectionTitle>

      {loading && (
        <EmptyBox>Loading linked folders…</EmptyBox>
      )}

      {!loading && folders.length === 0 && (
        <EmptyBox>
          No linked folders yet. Start on the Link Folders tab to connect a cloud folder
          with your drawing PDFs.
          {onGoToLinkFolders && (
            <PrimaryBtn type="button" onClick={onGoToLinkFolders}>
              Go to Link Folders
            </PrimaryBtn>
          )}
        </EmptyBox>
      )}

      {!loading && drawingFolders.length > 0 && (
        <FolderList>
          {drawingFolders.map((folder) => {
            const stats = folder.stats;
            const hasLabels = stats && stats.totalComponents > 0;
            const hasUnknowns = stats && stats.unknownsRemaining > 0;
            return (
              <FolderCard
                key={folder.requirementId}
                type="button"
                onClick={() => onSelectFolder(folder)}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <FolderName>{folder.title}</FolderName>
                  <FolderMeta>
                    {folder.drawingCount} drawing{folder.drawingCount === 1 ? '' : 's'}
                    {folder.fileCount > folder.drawingCount
                      ? ` · ${folder.fileCount} total files`
                      : ''}
                  </FolderMeta>
                  {stats && (
                    <StatRow>
                      {hasLabels && (
                        <StatPill>{stats.totalComponents.toLocaleString()} labels</StatPill>
                      )}
                      {stats.totalCrossRefs > 0 && (
                        <StatPill>{stats.totalCrossRefs} cross-refs</StatPill>
                      )}
                      {stats.sheetCount > 0 && (
                        <StatPill>{stats.sheetCount} sheet{stats.sheetCount === 1 ? '' : 's'}</StatPill>
                      )}
                    </StatRow>
                  )}
                </div>
                <Badge $warn={hasUnknowns} $ready={hasLabels && !hasUnknowns}>
                  {hasUnknowns
                    ? `${stats.unknownsRemaining} unmapped`
                    : hasLabels
                    ? 'Labeled'
                    : 'Open review'}
                </Badge>
              </FolderCard>
            );
          })}
        </FolderList>
      )}

      {!loading && otherFolders.length > 0 && drawingFolders.length === 0 && (
        <EmptyBox>
          {otherFolders.length} linked folder{otherFolders.length === 1 ? '' : 's'} found,
          but none contain drawing PDFs yet. Re-run the classifier and mark PDFs as
          Drawing deliverables.
          {onGoToLinkFolders && (
            <PrimaryBtn type="button" onClick={onGoToLinkFolders}>
              Go to Link Folders
            </PrimaryBtn>
          )}
        </EmptyBox>
      )}
    </Wrap>
  );
}
